import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Button, ConfirmDialog } from '@mlm/ui';
import { toast } from '@mlm/ui/components/toast';
import { backfillFreeSlots, type FreeSlotBackfillResult } from '../../lib/api';

/**
 * The counterpart of "free slot at signup" for members who signed up BEFORE
 * it was on: count them, then give each an active free slot under their own
 * sponsor, exactly as the signup would have. Lives on the Settings card the
 * switch is on, and only does anything while that switch is saved on.
 */
export function FreeSlotBackfill({ enabled, canWrite }: { enabled: boolean; canWrite: boolean }) {
  const [counted, setCounted] = useState<FreeSlotBackfillResult | null>(null);
  const [open, setOpen] = useState(false);
  const check = useMutation({
    mutationFn: () => backfillFreeSlots({ execute: false }),
    onSuccess: setCounted,
    meta: { errorMessage: 'Could not count the members. Save the setting on first.' },
  });
  const run = useMutation({
    mutationFn: () => backfillFreeSlots({ execute: true }),
    onSuccess: (r) => {
      setCounted(null);
      toast.success(
        `${r.activated} member${r.activated === 1 ? '' : 's'} got a free slot${
          r.failed.length ? `; ${r.failed.length} could not (see the audit log)` : ''
        }.`,
      );
    },
    meta: { errorMessage: 'The run did not go through. Nothing was changed.' },
  });
  const n = counted?.eligible ?? 0;

  return (
    <div className="space-y-2 rounded-md border bg-muted/20 p-3">
      <p className="text-sm font-medium">Members who signed up before this was on</p>
      <p className="text-xs text-muted-foreground">
        They have no slot until someone gives them one. Count them first; the run then gives each an
        active free slot at the membership above, under their own sponsor, with no entry commission.
        Staff accounts are skipped, and running it again finds nobody.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          size="sm"
          variant="outline"
          disabled={!enabled || !canWrite || check.isPending}
          onClick={() => check.mutate()}
        >
          {check.isPending ? 'Counting…' : 'Count members without a slot'}
        </Button>
        {counted && (
          <span className="text-sm">
            {n === 0
              ? 'Everyone has a slot.'
              : `${n} member${n === 1 ? '' : 's'} without a slot${
                  counted.sample.length
                    ? ` (${counted.sample
                        .slice(0, 3)
                        .map((m) => m.name || m.email)
                        .join(', ')}${n > 3 ? '…' : ''})`
                    : ''
                }`}
          </span>
        )}
        {counted && n > 0 && (
          <Button size="sm" disabled={!canWrite || run.isPending} onClick={() => setOpen(true)}>
            Give them a free slot
          </Button>
        )}
      </div>
      {!enabled && (
        <p className="text-xs text-muted-foreground">Switch the setting on and save it first.</p>
      )}
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Give every member without a slot a free slot?"
        description={`${n} member${n === 1 ? '' : 's'} will get an active free slot at the chosen membership, placed under their sponsor. A slot cannot be taken back in bulk afterwards.`}
        confirmLabel={`Give ${n} free slot${n === 1 ? '' : 's'}`}
        loading={run.isPending}
        onConfirm={async () => {
          await run.mutateAsync();
          setOpen(false);
        }}
      />
    </div>
  );
}
